import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { FORM_DISPLAY_MODUS, DisplayModus } from 'constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown, faCopy, faTrash, faPen } from '@fortawesome/free-solid-svg-icons'
import './woRequiredTools.css'
import {
  Checkbox,
  Collapsible,
  Flex,
  IconButton,
  Inline,
  Spinner,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@airbus/components-react/components'

import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import ModalCopyRequiredTools from './modalCopyRequiredTools'
import ModalRequiredTools from './modalRequiredTools'
import URL_APIS from 'utils/api/urlApis'
import ComponentsUNames from 'constants/componentsUNames'
import ModalConfirm from 'components/Modal/ModalConfim'
import useMutation from 'utils/hooks/useMutation'
import LocalStorage from 'utils/myLocalStorage'
import { RequiredTool, RequiredToolInput } from 'types/workorder/requiredTool'
import { Lang } from 'context/LangContext'
import Fieldset from 'components/Fieldset/fieldset'
import { approStore, execStore } from 'types/workorder/workorder'
import FormControl from 'components/FormFields/FormControl'
import urlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import ResponsiveTableCell from 'components/responsiveTableCell'

type Props = {
  lang: Lang
  oenumdoc: string | null
  oeType: string | undefined
  selectedResource: RequiredToolInput
  setSelectedResource: (rt: RequiredToolInput) => void
  approStore?: approStore
  execStore?: execStore
  displayMode: DisplayModus
  expandAll: boolean
  usedTools: never[]
}

type SelectedRequiredTool = RequiredTool & {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  date?: Date
  resourceType?: string
}

const WoRequiredTools = ({
  lang,
  oenumdoc,
  oeType,
  selectedResource,
  setSelectedResource,
  approStore,
  execStore,
  displayMode,
  expandAll,
  usedTools,
}: Props) => {
  const { t } = useTranslation()

  const [isModalCopyRequiredToolsOpen, setIsModalCopyRequiredToolsOpen] = useState<boolean>(false)
  const [isModalFormOpen, setIsModalFormOpen] = useState<boolean>(false)
  const [modalFormDisplayMode, setModalFormDisplayMode] = useState(DisplayModus.READ)

  const [data, setData] = useState<SelectedRequiredTool[]>()
  const { fetchData: fetchRequiredTools } = useQuery<RequiredTool[]>(urlApis.URL_WORKORDER_REQUIRED_TOOLS_GET)

  // Expand/collapse status of main component
  const [isExpanded, setIsExpanded] = useState(false)
  // Data grouped by type: {<Type_1>: [{...}, {...}], <Type_2>: [{...}, {...}]}
  const [dataGroupedByType, setDataGroupedByType] = useState({})
  // Expand/collapse status of grouped-by type tables
  const [isExpandedGroupedByType, setIsExpandedGroupedByType] = useState({})
  const [requiredToolsToDelete, setRequiredToolsToDelete] = useState<SelectedRequiredTool | null>(null)

  const { mutate: deleteRequiredTools } = useMutation(URL_APIS.URL_WORKORDER_REQUIRED_TOOLS_DELETE)

  const canUpdate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        )))

  const canCreate = canUpdate

  // Hide column stock appro-location if no appro location or appro-location is the same as execution-store
  const showStockMagasinAppro = !(
    (approStore?.company === undefined && approStore?.store === undefined) ||
    (approStore?.company === execStore?.company && approStore.store === execStore.store)
  )

  const loadData = async () => {
    if (oenumdoc) {
      const response = await fetchRequiredTools({
        lang: lang,
        oenumdoc: oenumdoc,
      })
      setData(response?.data?.content)
    }
  }

  /**
   * Load required resources of Work-order
   */
  useEffect(() => {
    if (isExpanded) {
      loadData()
    } else {
      setData([])
    }
  }, [lang, isExpanded, selectedResource])

  /**
   * Group by data (required resources) by type (keep ordering as it is)
   * And compute extra properties to be shown in the table:
   * - stockExecTotal  = sum(stockExec, stockExecInter if > 0)
   * - stockApproTotal = sum(stockAppro, stockApproInter if > 0)
   */
  useEffect(() => {
    if (data) {
      const dataGroupByType = {}
      const isExpandedGroupByType = {}
      data.forEach((objResource) => {
        // Sum-up stocks
        // ..stocks execution
        if (Object.prototype.hasOwnProperty.call(objResource, 'stockExecInter') && objResource['stockExecInter'] > 0) {
          if (Object.prototype.hasOwnProperty.call(objResource, 'stockExec') && objResource['stockExec'] !== null) {
            objResource['stockExecTotal'] = objResource['stockExec'] + objResource['stockExecInter']
          } else {
            objResource['stockExecTotal'] = objResource['stockExecInter']
          }
        } else {
          if (Object.prototype.hasOwnProperty.call(objResource, 'stockExec') && objResource['stockExec'] !== null) {
            objResource['stockExecTotal'] = objResource['stockExec']
          } else {
            objResource['stockExecTotal'] = null
          }
        }
        // ..stocks appro.
        if (
          Object.prototype.hasOwnProperty.call(objResource, 'stockApproInter') &&
          objResource['stockApproInter'] > 0
        ) {
          if (Object.prototype.hasOwnProperty.call(objResource, 'stockAppro') && objResource['stockAppro'] !== null) {
            objResource['stockApproTotal'] = objResource['stockAppro'] + objResource['stockApproInter']
          } else {
            objResource['stockApproTotal'] = objResource['stockApproInter']
          }
        } else {
          if (Object.prototype.hasOwnProperty.call(objResource, 'stockAppro') && objResource['stockAppro'] !== null) {
            objResource['stockApproTotal'] = objResource['stockAppro']
          } else {
            objResource['stockApproTotal'] = null
          }
        }

        objResource.checked = false

        // Group-by
        if (Object.prototype.hasOwnProperty.call(dataGroupByType, objResource['libType'])) {
          dataGroupByType[objResource['libType']].push(objResource)
        } else {
          dataGroupByType[objResource['libType']] = [objResource]
          isExpandedGroupByType[objResource['libType']] = isExpanded
        }
      })
      setDataGroupedByType(dataGroupByType)
      setIsExpandedGroupedByType(isExpandedGroupByType)
    } else {
      setDataGroupedByType({})
      setIsExpandedGroupedByType({})
    }
  }, [data])

  /**
   * ExpandAll and collapseAll are mirror-toggled, so a hook on expandAll is enough
   */
  useEffect(() => {
    setIsExpanded(expandAll)

    if (JSON.stringify(isExpandedGroupedByType) !== '{}') {
      const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
      Object.keys(copyIsExpandedGroupedByType).map((it) => (copyIsExpandedGroupedByType[it] = expandAll))
      setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
    }
  }, [expandAll])

  const handleSubSectionToggleExpandCollapse = (evt) => {
    const actionName = evt.currentTarget.attributes['name'].nodeValue
    const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
    copyIsExpandedGroupedByType[actionName] = !isExpandedGroupedByType[actionName]
    setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
  }

  const handleCheck = (event, resourceType, j) => {
    const nextResource = dataGroupedByType[resourceType].map((r, i) =>
      i === j ? { ...r, checked: event.target.checked } : r,
    )
    setDataGroupedByType({ ...dataGroupedByType, [resourceType]: nextResource })
  }

  const handleCheckAll = (event, resourceType) => {
    const nextResource = dataGroupedByType[resourceType].map((r) => ({
      ...r,
      checked: usedTools?.find((ut) => ut.pn === r.pn) ? r.checked : event.target.checked,
    }))
    setDataGroupedByType({ ...dataGroupedByType, [resourceType]: nextResource })
  }

  const handleRessourceCopy = (resourceType, j) => {
    const nextResource = dataGroupedByType[resourceType].map((r, i) =>
      i === j ? { ...r, checked: true } : { ...r, checked: false },
    )
    setDataGroupedByType({ ...dataGroupedByType, [resourceType]: nextResource })
    setIsModalCopyRequiredToolsOpen(true)
  }

  const handleModalCopyRequiredToolsClose = () => {
    setIsModalCopyRequiredToolsOpen(false)
    loadData()
  }

  const handleModalFormPostSubmit = () => {
    setIsModalFormOpen(false)
    setSelectedResource(undefined)

    loadData()
  }

  const handleDelete = async () => {
    if (!requiredToolsToDelete) return
    setRequiredToolsToDelete({ ...requiredToolsToDelete, loading: true, open: false })
    await deleteRequiredTools({
      params: { reference: requiredToolsToDelete.reference, pn: requiredToolsToDelete.pn },
      method: 'DELETE',
    })

    // await setTimeout(() => {
    setRequiredToolsToDelete(null)
    loadData()
    // }, 2000)
  }

  const handleCreateRequiredTool = () => {
    setSelectedResource({
      oncodte: '',
      ontype: '',
      onappl: '',
      reference: oenumdoc as string,
      onrev: '',
      onord: '',
      pn: '',
      designation: '',
      quantite: 1,
      codeType: '',
      libType: '',
    })
    setModalFormDisplayMode(DisplayModus.CREATE)
    setIsModalFormOpen(true)
  }

  const handleUpdateRequiredTool = (nextResource: SelectedRequiredTool) => {
    setSelectedResource(nextResource)
    setModalFormDisplayMode(DisplayModus.WRITE)
    setIsModalFormOpen(true)
  }

  return (
    <div style={{ width: '100%', marginTop: '50px' }}>
      <Collapsible title={t('9500189')} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
            }}
          >
            {canUpdate &&
              displayMode === FORM_DISPLAY_MODUS.EXEC &&
              JSON.stringify(dataGroupedByType) !== '{}' &&
              JSON.stringify(dataGroupedByType) !== '[]' && (
                <IconButton
                  size='small'
                  onClick={() => setIsModalCopyRequiredToolsOpen(true)}
                  title={t('copy_used_resources')}
                  variant='ghost'
                >
                  <FontAwesomeIcon icon={faCopy} />
                </IconButton>
              )}
            <FormControl label={t('1CMG001')} readOnly>
              <Flex spacing='1-x' childrenStyle={{ flex: 1 }}>
                <Typography>
                  {execStore?.third
                    ? execStore?.third
                    : execStore?.company === undefined && execStore?.store === undefined
                      ? '-'
                      : execStore.company + '/' + execStore.store}
                </Typography>
              </Flex>
            </FormControl>
            {!(execStore?.company === approStore?.company && execStore?.store === approStore?.store) && (
              <FormControl label={t('7000973')} readOnly>
                <Flex spacing='1-x' childrenStyle={{ flex: 1 }}>
                  <Typography>
                    {approStore?.company === undefined && approStore?.store === undefined
                      ? '-'
                      : approStore.company + '/' + approStore.store}
                  </Typography>
                </Flex>
              </FormControl>
            )}
          </div>
          <div>
            {canCreate && (
              <IconButton
                variant='ghost'
                size='small'
                onClick={handleCreateRequiredTool}
                style={{ marginBottom: '1em' }}
              >
                <FontAwesomeIcon icon={faSquarePlus} />
              </IconButton>
            )}
          </div>
          {JSON.stringify(dataGroupedByType) !== '{}' && JSON.stringify(dataGroupedByType) !== '[]' ? (
            <div>
              {Object.keys(dataGroupedByType).map((resourceType, i) => {
                const filteredData = dataGroupedByType[resourceType].filter(
                  (d) => !usedTools?.find((ut) => ut.pn === d.pn),
                )
                const displayCheckBox = Array.isArray(filteredData) && displayMode === FORM_DISPLAY_MODUS.EXEC

                return (
                  <Fieldset
                    legend={
                      <>
                        {isExpandedGroupedByType[resourceType] ? (
                          <FontAwesomeIcon
                            name={resourceType}
                            icon={faChevronUp}
                            onClick={handleSubSectionToggleExpandCollapse}
                          />
                        ) : (
                          <FontAwesomeIcon
                            name={resourceType}
                            icon={faChevronDown}
                            onClick={handleSubSectionToggleExpandCollapse}
                          />
                        )}
                        {resourceType}
                      </>
                    }
                    key={'resourcesRequiredType_' + i}
                  >
                    <div
                      style={{
                        display: isExpandedGroupedByType[resourceType] ? 'inherit' : 'none',
                      }}
                    >
                      <Table>
                        <colgroup>
                          {displayCheckBox && <col style={{ width: '3%' }} />}
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '9%' }} />
                        </colgroup>
                        <TableHead>
                          <TableRow>
                            {displayCheckBox && (
                              <ResponsiveTableCell style={{ textAlign: 'center' }}>
                                <Checkbox
                                  checked={filteredData.length !== 0 && filteredData.every((r) => r.checked)}
                                  indeterminate={filteredData?.some((r) => r.checked !== filteredData[0].checked)}
                                  onChange={(event) => handleCheckAll(event, resourceType)}
                                  disabled={filteredData.length === 0}
                                  sx={{ padding: 0 }}
                                />
                              </ResponsiveTableCell>
                            )}
                            <ResponsiveTableCell>{t('9500047')}</ResponsiveTableCell>
                            <ResponsiveTableCell>{t('7000248')}</ResponsiveTableCell>
                            <ResponsiveTableCell align='right'>{t('7000199')}</ResponsiveTableCell>
                            <ResponsiveTableCell align='right'>{t('7001180')}</ResponsiveTableCell>
                            <ResponsiveTableCell
                              align='right'
                              style={{
                                width: '9vw',
                                display: showStockMagasinAppro ? 'table-cell' : 'none',
                              }}
                            >
                              {t('9500190')}
                            </ResponsiveTableCell>
                            <ResponsiveTableCell>{t('1P00425')}</ResponsiveTableCell>
                            <ResponsiveTableCell>{t('9500191')}</ResponsiveTableCell>
                            <ResponsiveTableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dataGroupedByType[resourceType].map((objResource: SelectedRequiredTool, j) => (
                            <TableRow key={'resourcesRequiredTypeRow_' + i + '_' + j}>
                              {displayMode === FORM_DISPLAY_MODUS.EXEC && (
                                <ResponsiveTableCell style={{ textAlign: 'center' }}>
                                  <Checkbox
                                    checked={objResource.checked}
                                    onChange={(event) => handleCheck(event, resourceType, j)}
                                    disabled={usedTools?.find((ut) => ut.pn === objResource.pn)}
                                    title={t('disable_resource_copy')}
                                    sx={{ padding: 0 }}
                                  />
                                </ResponsiveTableCell>
                              )}
                              <ResponsiveTableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'pn') ? objResource['pn'] : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'designation')
                                  ? objResource['designation']
                                  : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                data-negative={objResource.quantite ? objResource.quantite < 0 : false}
                                align='right'
                              >
                                {Object.prototype.hasOwnProperty.call(objResource, 'quantite')
                                  ? objResource['quantite']
                                  : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                data-negative={objResource.stockExec ? objResource.stockExec < 0 : false}
                                align='right'
                                className={
                                  objResource.stockExecInter !== undefined && objResource.stockExecInter > 0
                                    ? 'has_interchangeable'
                                    : ''
                                }
                              >
                                {objResource['stockExecTotal'] !== null ? objResource['stockExecTotal'] : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                data-negative={objResource.stockAppro ? objResource.stockAppro < 0 : false}
                                align='right'
                                className={
                                  objResource.stockApproInter !== undefined && objResource.stockApproInter > 0
                                    ? 'has_interchangeable'
                                    : ''
                                }
                                style={{
                                  display: showStockMagasinAppro ? 'table-cell' : 'none',
                                }}
                              >
                                {objResource['stockApproTotal'] !== null ? objResource['stockApproTotal'] : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell
                                className={
                                  Object.prototype.hasOwnProperty.call(objResource, 'reqForm') &&
                                  objResource.reqForm?.pn !== objResource.pn
                                    ? 'has_interchangeable'
                                    : ''
                                }
                              >
                                {!Object.prototype.hasOwnProperty.call(objResource, 'reqForm') ||
                                objResource.reqForm?.numdoc === null
                                  ? '-'
                                  : objResource.reqForm.numdoc +
                                    (Object.prototype.hasOwnProperty.call(objResource.reqForm, 'item') &&
                                    objResource.reqForm.item !== null
                                      ? '/' + objResource.reqForm.item
                                      : '')}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell>
                                {Object.prototype.hasOwnProperty.call(objResource, 'reqForm') &&
                                objResource.reqForm?.status !== null
                                  ? objResource.reqForm.status
                                  : '-'}
                              </ResponsiveTableCell>
                              <ResponsiveTableCell>
                                <Inline spacing='1-x'>
                                  {canUpdate && displayMode === FORM_DISPLAY_MODUS.EXEC && (
                                    <IconButton
                                      onClick={() => handleRessourceCopy(resourceType, j)}
                                      size='small'
                                      variant='ghost'
                                      disabled={usedTools?.find((ut) => ut.pn === objResource.pn)}
                                      title={t('copy_tools', { pn: objResource.pn })}
                                    >
                                      <FontAwesomeIcon icon={faCopy} size='xs' />
                                    </IconButton>
                                  )}
                                  {canUpdate && (
                                    <Inline spacing='1-x'>
                                      <IconButton
                                        size='small'
                                        variant='ghost'
                                        onClick={() => handleUpdateRequiredTool(objResource)}
                                      >
                                        <FontAwesomeIcon size='xs' icon={faPen} />
                                      </IconButton>
                                      <IconButton
                                        size='small'
                                        variant='ghost'
                                        onClick={() => setRequiredToolsToDelete({ ...objResource, open: true })}
                                      >
                                        {objResource?.pn === requiredToolsToDelete?.pn &&
                                        requiredToolsToDelete?.loading ? (
                                          <Spinner size='small' />
                                        ) : (
                                          <FontAwesomeIcon size='xs' icon={faTrash} />
                                        )}
                                      </IconButton>
                                    </Inline>
                                  )}
                                </Inline>
                              </ResponsiveTableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </Fieldset>
                )
              })}
            </div>
          ) : (
            <div style={{ paddingLeft: '1em' }}>{t('9500055')}</div>
          )}
        </div>
      </Collapsible>

      <ModalCopyRequiredTools
        lang={lang}
        open={isModalCopyRequiredToolsOpen}
        resources={dataGroupedByType}
        setSelectedResource={setSelectedResource}
        onClose={handleModalCopyRequiredToolsClose}
        onSubmit={() => {
          setIsModalCopyRequiredToolsOpen(false)
          setSelectedResource(null)
        }}
        oenumdoc={oenumdoc as string}
      />
      <ModalConfirm
        isOpened={Boolean(requiredToolsToDelete?.open)}
        onCloseModal={() => setRequiredToolsToDelete(null)}
        confirmMessage={t('9510089')}
        onSubmit={handleDelete}
      />
      <ModalRequiredTools
        lang={lang}
        open={isModalFormOpen}
        displayMode={modalFormDisplayMode}
        onClose={() => setIsModalFormOpen(false)}
        resource={selectedResource}
        onPostSubmit={handleModalFormPostSubmit}
        oenumdoc={oenumdoc}
      />
    </div>
  )
}

export default WoRequiredTools
